import axios from "axios";
import { refreshTokenAPI } from "../../../Redux/Apis/authApi";
import { clearToken, setToken } from "../../../Redux/Slices/authSlice";
import { decryptData, encryptData, getClientSource } from "./Functions";
import { BASE_URL } from "./baseUrl";

const protectedApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  },
});

const getStore = () => {
  const store = require("../../../Redux/store").store;
  return store;
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

protectedApi.interceptors.request.use(
  async (config) => {
    const requestKey = config.url || "";
    let store = getStore();
    let state = store.getState();

    const accessToken =
      localStorage.getItem("accessToken") ?? state.auth.accessToken;

    // Multipart uploads carry the PDF as a file; only encrypt the payload field.
    if (config.data instanceof FormData) {
      config._dataEncrypted = true;
      delete config.headers["Content-Type"];
    } else if (config.data && !config._dataEncrypted) {
      config.data = encryptData(config.data);
      config._dataEncrypted = true; // Mark data as encrypted
      config.headers["Content-Type"] = "text/plain";
    }
    config.headers["company_source"] = getClientSource();
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

protectedApi.interceptors.response.use(
  (response) => {
    const body = response.data;
    const isEmptyBody =
      body == null || (typeof body === "string" && body.trim() === "");

    if ((response.status === 304 || isEmptyBody) && !response.config._cacheRetry) {
      const retryConfig = {
        ...response.config,
        _cacheRetry: true,
        headers: {
          ...response.config.headers,
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      };
      delete retryConfig.headers["If-None-Match"];
      delete retryConfig.headers["If-Modified-Since"];
      return protectedApi(retryConfig);
    }

    if (response.status === 304 || isEmptyBody) {
      return Promise.reject(new Error("Empty API response"));
    }

    const decryptRes = decryptData(body);
    response.data = decryptRes;
    return response;
  },
  async (error) => {
    error.response.data = decryptData(error.response.data);
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 403 &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
            }
            return protectedApi(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        let store = getStore();
        let state = store.getState();
        const refreshToken =
          state.auth.refreshToken ?? localStorage.getItem("refreshToken");
        getStore()
          .dispatch(refreshTokenAPI({ refreshToken: refreshToken }))
          .then(({ payload }) => {
            const { accessToken } = payload.data;
            getStore().dispatch(setToken(accessToken));

            protectedApi.defaults.headers[
              "Authorization"
            ] = `Bearer ${accessToken}`;
            if (originalRequest.headers) {
              originalRequest.headers[
                "Authorization"
              ] = `Bearer ${accessToken}`;
            }
            processQueue(null, accessToken);
            resolve(protectedApi(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            window.location.href = "/login";
            getStore().dispatch(clearToken());
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    if (
      error.response &&
      error.response.status === 401 &&
      error.code !== "ERR_CANCELED"
    ) {
      window.location.href = "/login";
      getStore().dispatch(clearToken());
    }

    return Promise.reject(error);
  }
);

export { protectedApi };
